import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { describeError, useToast } from "../toast";
import { useTranslation } from "react-i18next";
import { EditDrawer, type Dismiss } from "../EditDrawer";
import { PermissionGate } from "../../auth/access";
import {
  CONTENT_OWNER_KIND_OPTIONS,
  foyerApi,
  type ContentOwnerKindValue,
  type ContentOwnerRecord,
  type ContentSupplierRow,
} from "../../http/foyer";
import type { SignagePlaylist } from "@shared/types/signage";

/**
 * The dialogs a content feed and a content-owner login are edited with -
 * shared by the Content feeds page (feeds without a company) and the
 * Companies page's settings (a company's own feeds and login), since a feed
 * with a company is governed from that company (Sigge, 2026-10-19).
 */

/**
 * A live preview only — a mirror of the server's own slugify
 * (`mintProviderId` in `contentPackages.ts`, `contentOwnerSlug` in
 * `@shared/content-owner`), not a shared import of either.
 *
 * `@shared/content-owner` is not in `foyer-shared/package.json`'s `exports`
 * map, so a runtime import of it here would typecheck fine under the
 * monorepo lane and then fail to resolve in the standalone build that
 * actually ships (see `global/pitfalls/shared-alias-build-lanes.md`) — a
 * type-only import is erased and safe, a value import is not. The server is
 * still the actual source of truth and derives its own copy independently;
 * this only has to look right while typing.
 */
export function slugPreview(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * A client-side mirror of `isMrssFeedUrl` (`@shared/content-owner`), same
 * caveat as `slugPreview` above: a value import of that module is not safe in
 * the standalone build that actually ships. The server stays the one
 * authoritative check.
 */
function looksLikeMrssFeedUrl(value: string): boolean {
  if (value.trim() === "") return true; // empty is valid - it means "no feed"
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Mint a real provider — never possible anywhere before this (`[[mrss-feed-content-providers]]`).
 * The legacy portal's own "add provider" tab writes a tag, not a row a
 * package rule can carry, which is exactly how Tigra ended up unusable.
 *
 * Created **untrusted** and with no login. Trust and login are both separate,
 * deliberate switches — minting the row is only the first act.
 */
export function AddSupplierDialog({
  onClose,
  onCreated,
  company,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
  /** Create the feed straight into this company (from its settings). */
  company?: { id: string; name: string } | null;
}) {
  const [name, setName] = useState("");
  const [providerId, setProviderId] = useState("");
  // Once the operator types into the id field directly, it stops following
  // the name — same rule as every other auto-derived field in this file.
  const [idEdited, setIdEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);

  const save = async () => {
    setSaving(true);
    setFieldError(null);
    try {
      await foyerApi.createContentSupplier({
        name: name.trim(),
        // Undefined unless the operator deliberately typed one: the preview
        // is only a preview, and the server's own mintProviderId stays the
        // one canonical algorithm rather than two copies that could drift.
        providerId: idEdited ? providerId.trim() || undefined : undefined,
        ...(company ? { companyId: company.id } : {}),
      });
      toast.success(
        company
          ? t("contentFeedDialogs.createdFor", {
              name: name.trim(),
              company: company.name,
            })
          : t("contentFeedDialogs.created", { name: name.trim() })
      );
      await onCreated();
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "already_taken" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: err.body.hint ?? t("contentFeedDialogs.alreadyTaken"),
        });
      } else if (err.code === "invalid" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: t("contentFeedDialogs.notAccepted"),
        });
      } else {
        toast.error(
          describeError(t, "createContentFeed", e, { name: name.trim() })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {company
          ? t("contentFeedDialogs.addTitleFor", { company: company.name })
          : t("contentFeedDialogs.addTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("common:labels.name")}
            value={name}
            onChange={(e) => {
              const next = e.target.value;
              setName(next);
              if (!idEdited) setProviderId(slugPreview(next));
            }}
            size="small"
            required
            autoFocus
            error={fieldError?.field === "name"}
            helperText={
              fieldError?.field === "name"
                ? fieldError.hint
                : t("contentFeedDialogs.nameHelp")
            }
            inputProps={{ "data-testid": "supplier-name" }}
          />

          <TextField
            label={t("common:labels.id")}
            value={providerId}
            onChange={(e) => {
              setProviderId(e.target.value);
              setIdEdited(true);
            }}
            size="small"
            error={fieldError?.field === "providerId"}
            helperText={
              fieldError?.field === "providerId"
                ? fieldError.hint
                : t("contentFeedDialogs.idHelp")
            }
            inputProps={{ "data-testid": "supplier-id" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || name.trim() === ""}
          data-testid="supplier-save"
        >
          {t("common:buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

/** Basis points as a percentage, for a human reading the dialog. */
function sharePercent(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

/**
 * Edit an existing login, in place on the provider row — replaces the
 * separate "content owners" card the first cut of this page had. See the
 * module doc comment for why that was wrong.
 *
 * The two fields NOT here, deliberately: `brand`/`identity` are the
 * supplier's OWN identity, owner-editable only at `PATCH /owner/branding` —
 * an operator quietly correcting a studio's copyright line is a divergence
 * the studio cannot see. There is no delete: `enabled: false` ends the
 * relationship reversibly, and it is also what keeps the underlying content
 * provider from ever being deletable again (see the delete button above).
 */
export function EditLoginDialog({
  owner,
  onClose,
  onSaved,
}: {
  owner: ContentOwnerRecord;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(owner.name);
  const [slug, setSlug] = useState(owner.slug);
  const [contactEmail, setContactEmail] = useState(owner.contactEmail);
  const [enabled, setEnabled] = useState(owner.enabled);
  // `""` only for an owner created before `kind` existed; once set it can be
  // changed but not cleared, so the empty choice is not offered back.
  const [kind, setKind] = useState<ContentOwnerKindValue | "">(
    owner.kind ?? ""
  );
  const [share, setShare] = useState(
    owner.revenueShareBps === null ? "" : String(owner.revenueShareBps)
  );
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);

  const shareBps = share.trim() === "" ? null : Number(share.trim());
  const shareValid =
    shareBps === null ||
    (Number.isInteger(shareBps) && shareBps >= 0 && shareBps <= 10000);

  const save = async (dismiss: Dismiss) => {
    setSaving(true);
    setFieldError(null);
    try {
      await foyerApi.updateContentOwner(owner.id, {
        name: name.trim(),
        slug: slug.trim(),
        contactEmail: contactEmail.trim(),
        enabled,
        ...(kind ? { kind } : {}),
        revenueShareBps: shareBps,
      });
      toast.success(
        t("contentFeedDialogs.loginUpdated", { name: name.trim() })
      );
      // Slide away first; the page then reloads and unmounts the drawer.
      dismiss(() => void onSaved());
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "already_taken" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: err.body.hint ?? t("contentFeedDialogs.alreadyTaken"),
        });
      } else if (err.code === "invalid" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: t("contentFeedDialogs.notAccepted"),
        });
      } else {
        toast.error(
          describeError(t, "saveOwnerLogin", e, { owner: owner.name })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditDrawer
      open
      onClose={onClose}
      busy={saving}
      title={t("contentFeedDialogs.loginTitle", { name: owner.name })}
      data-testid="edit-login-drawer"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="content-owners"
            level="administrate"
            place={{ companyId: owner.id }}
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              onClick={() => void save(dismiss)}
              disabled={saving || name.trim() === "" || !shareValid}
              data-testid="edit-login-save"
            >
              {t("common:buttons.save")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label={t("contentFeedDialogs.orgName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          required
          autoFocus
          error={fieldError?.field === "name"}
          helperText={
            fieldError?.field === "name"
              ? fieldError.hint
              : t("contentFeedDialogs.orgNameHelp")
          }
          inputProps={{ "data-testid": "edit-login-name" }}
        />

        <TextField
          label={t("contentFeedDialogs.handle")}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          size="small"
          error={fieldError?.field === "slug"}
          helperText={
            fieldError?.field === "slug"
              ? fieldError.hint
              : // 🚨 The storefront handle is live in every magic link
                // already sent (`WL-7`). Changing it is not a rename.
                t("contentFeedDialogs.handleHelp")
          }
          inputProps={{ "data-testid": "edit-login-slug" }}
        />

        <TextField
          select
          label={t("contentFeedDialogs.kind")}
          value={kind}
          onChange={(e) => setKind(e.target.value as ContentOwnerKindValue)}
          size="small"
          error={fieldError?.field === "kind"}
          helperText={
            kind === ""
              ? t("contentFeedDialogs.kindUnset")
              : t("contentFeedDialogs.kindHelp")
          }
          inputProps={{ "data-testid": "edit-login-kind" }}
        >
          {CONTENT_OWNER_KIND_OPTIONS.map((k) => (
            <MenuItem key={k.value} value={k.value}>
              {t(`contentFeedDialogs.ownerKind.${k.value}`)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t("contentFeedDialogs.contactEmail")}
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          size="small"
          helperText={t("contentFeedDialogs.contactEmailHelp")}
          inputProps={{ "data-testid": "edit-login-contact" }}
        />

        <TextField
          label={t("contentFeedDialogs.revenueShare")}
          value={share}
          onChange={(e) => setShare(e.target.value)}
          size="small"
          error={!shareValid || fieldError?.field === "revenueShareBps"}
          helperText={
            !shareValid
              ? t("contentFeedDialogs.revenueShareInvalid")
              : fieldError?.field === "revenueShareBps"
                ? fieldError.hint
                : shareBps === null
                  ? t("contentFeedDialogs.revenueShareEmpty")
                  : t("contentFeedDialogs.revenueShareValue", {
                      percent: sharePercent(shareBps),
                    })
          }
          inputProps={{ "data-testid": "edit-login-share" }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            inputProps={
              { "aria-label": t("contentFeedDialogs.enabled") } as never
            }
            data-testid="edit-login-enabled"
          />
          <Typography variant="body2">
            {enabled
              ? t("contentFeedDialogs.enabledText")
              : t("contentFeedDialogs.disabledText")}
          </Typography>
        </Stack>
      </Stack>
    </EditDrawer>
  );
}

/**
 * Attach, change or clear a provider's MRSS feed — Type C in the A/B/C shape
 * (`[[mrss-feed-content-providers]]`).
 *
 * Deliberately its OWN dialog, not a field on `EditLoginDialog` — ruled the
 * same day the field first landed on the login and moved: a feed-only
 * aggregator with nobody at the source ever logging in still needs one, and
 * putting it behind "edit login" would put it behind a login that provider
 * will never have. This dialog opens for ANY provider row, `hasLogin` or not.
 */
export function EditFeedDialog({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: ContentSupplierRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [mrssFeedUrl, setMrssFeedUrl] = useState(supplier.mrssFeedUrl ?? "");
  const [effectChannelId, setEffectChannelId] = useState(
    supplier.effectChannelId ?? ""
  );
  const [channels, setChannels] = useState<SignagePlaylist[] | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [channelError, setChannelError] = useState<string | null>(null);
  const clearing = mrssFeedUrl.trim() === "";

  const mrssFeedUrlValid = looksLikeMrssFeedUrl(mrssFeedUrl);
  // Reported by Sigge from production: a feed URL was saved with no channel
  // ever chosen, and every scan silently read the whole feed as "new"
  // forever. The server now refuses to save that combination outright - this
  // mirrors the same rule here so the operator sees it before clicking Save,
  // not after.
  const channelMissing = !clearing && !effectChannelId;

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn({ kind: "global" })
      .then((rows) => {
        if (!alive) return;
        setChannels(rows.filter((p) => p.scope === "global" && p.effect));
      })
      .catch(() => alive && setChannels([]));
    return () => {
      alive = false;
    };
  }, []);

  const save = async (dismiss: Dismiss) => {
    setSaving(true);
    setFieldError(null);
    setChannelError(null);
    try {
      // Both fields in ONE call, atomically - never a URL saved with the
      // channel choice as a second request that could be skipped. That
      // sequential pattern is exactly how the production row went wrong.
      await foyerApi.setSupplierMrssFeed(
        supplier.id,
        clearing ? null : mrssFeedUrl.trim(),
        effectChannelId || null
      );
      toast.success(
        t("contentFeedDialogs.feedUpdated", { name: supplier.name })
      );
      // Slide away first; the page then reloads and unmounts the drawer.
      dismiss(() => void onSaved());
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "effect_channel_required") {
        setChannelError(
          err.body?.hint ?? t("contentFeedDialogs.chooseChannel")
        );
      } else if (
        err.code === "invalid" &&
        err.body?.field === "effectChannelId"
      ) {
        setChannelError(
          err.body.hint ?? t("contentFeedDialogs.invalidEffectChannel")
        );
      } else if (err.code === "invalid" && err.body?.field === "mrssFeedUrl") {
        // Every other refusal on this route names the URL field - the check
        // that fetches the feed and confirms it looks like RSS/MRSS, not
        // just that it parses as a URL (`[[mrss-feed-content-providers]]`).
        setFieldError(err.body.hint ?? t("contentFeedDialogs.feedNotVerified"));
      } else {
        toast.error(
          describeError(t, "saveFeedSettings", e, { supplier: supplier.name })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditDrawer
      open
      onClose={onClose}
      busy={saving}
      title={t("contentFeedDialogs.feedTitle", { name: supplier.name })}
      data-testid="edit-feed-drawer"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={saving || !mrssFeedUrlValid || channelMissing}
            data-testid="edit-feed-save"
          >
            {saving
              ? clearing
                ? t("common:states.saving")
                : t("contentFeedDialogs.verifying")
              : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Alert severity="info">{t("contentFeedDialogs.feedIntro")}</Alert>

        <TextField
          label={t("contentFeedDialogs.feedUrl")}
          value={mrssFeedUrl}
          onChange={(e) => {
            setMrssFeedUrl(e.target.value);
            setFieldError(null);
          }}
          size="small"
          autoFocus
          error={!mrssFeedUrlValid || !!fieldError}
          helperText={
            fieldError
              ? fieldError
              : !mrssFeedUrlValid
                ? t("contentFeedDialogs.feedUrlInvalid")
                : t("contentFeedDialogs.feedUrlHelp")
          }
          inputProps={{ "data-testid": "edit-feed-url" }}
        />

        <TextField
          select
          label={t("contentFeedDialogs.effectChannel")}
          value={effectChannelId}
          onChange={(e) => {
            setEffectChannelId(e.target.value);
            setChannelError(null);
          }}
          size="small"
          disabled={!channels}
          required={!clearing}
          error={!!channelError || channelMissing}
          helperText={
            channelError
              ? channelError
              : clearing
                ? t("contentFeedDialogs.effectChannelClearing")
                : channelMissing
                  ? t("contentFeedDialogs.effectChannelRequired")
                  : t("contentFeedDialogs.effectChannelHelp")
          }
          inputProps={{ "data-testid": "edit-feed-effect-channel" }}
        >
          {(channels ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </EditDrawer>
  );
}
